import { createContext, useContext, useReducer, useEffect } from 'react'

const AssessmentContext = createContext()

export const SESSION_KEY = 'betterStep_session'

const initialState = {
  currentScreen: 'welcome',
  userAge: null,

  // Onboarding, mirroring the production flow
  selectedAddictions: [],
  gates: {}, // slug → { status: 'active'|'recovery', frequency, control, daysClean }
  currentAreaIndex: 0,
  primaryAddiction: null,
  primaryOverridden: false,
  screenerAnswers: {}, // question index → option index
  currentScreenerIndex: 0,
  barc10Answers: {}, // id → 0..6
  currentBarcIndex: 0,

  // Derived
  addictionResults: {},
  barc10Score: null,

  // DASS-21 (short form)
  dassAnswers: {},
  dassScores: null,
  currentDASSIndex: 0,

  // Full assessment (offered from the readout)
  extendedAssessmentComplete: false,
  extendedPhase: 'dass',
  part2DassAnswers: {},
  profileAnswers: {},
  currentPart2DASSIndex: 0,
  currentProfileIndex: 0,

  // Results
  investorMetrics: null,
  resultsId: null,
  submittedEmail: null,
}

function assessmentReducer(state, action) {
  switch (action.type) {
    case 'HYDRATE':
      return { ...initialState, ...action.payload }

    case 'SET_SCREEN':
      return { ...state, currentScreen: action.payload }

    case 'SET_AGE':
      return { ...state, userAge: action.payload }

    case 'TOGGLE_AREA': {
      const slug = action.payload
      const on = state.selectedAddictions.includes(slug)
      const selectedAddictions = on
        ? state.selectedAddictions.filter((s) => s !== slug)
        : [...state.selectedAddictions, slug]
      const gates = { ...state.gates }
      if (on) delete gates[slug]
      // Deselecting the primary clears it, so it can never point at an area
      // the user no longer has.
      const primaryAddiction = on && state.primaryAddiction === slug ? null : state.primaryAddiction
      return { ...state, selectedAddictions, gates, primaryAddiction }
    }

    case 'SET_GATE':
      return {
        ...state,
        gates: { ...state.gates, [action.payload.slug]: { ...state.gates[action.payload.slug], ...action.payload.gate } },
      }

    case 'NEXT_AREA':
      return { ...state, currentAreaIndex: state.currentAreaIndex + 1 }

    case 'PREV_AREA':
      return { ...state, currentAreaIndex: Math.max(0, state.currentAreaIndex - 1) }

    case 'SET_AREA_INDEX':
      return { ...state, currentAreaIndex: Math.max(0, action.payload) }

    case 'SET_PRIMARY':
      return { ...state, primaryAddiction: action.payload.slug, primaryOverridden: !!action.payload.overridden }

    case 'ANSWER_SCREENER':
      return {
        ...state,
        screenerAnswers: { ...state.screenerAnswers, [action.payload.index]: action.payload.option },
      }

    case 'NEXT_SCREENER':
      return { ...state, currentScreenerIndex: state.currentScreenerIndex + 1 }

    case 'PREV_SCREENER':
      return { ...state, currentScreenerIndex: Math.max(0, state.currentScreenerIndex - 1) }

    case 'ANSWER_BARC':
      return { ...state, barc10Answers: { ...state.barc10Answers, [action.payload.id]: action.payload.value } }

    case 'NEXT_BARC':
      return { ...state, currentBarcIndex: state.currentBarcIndex + 1 }

    case 'PREV_BARC':
      return { ...state, currentBarcIndex: Math.max(0, state.currentBarcIndex - 1) }

    case 'SET_AREA_RESULTS':
      return { ...state, addictionResults: action.payload }

    case 'SET_BARC_SCORE':
      return { ...state, barc10Score: action.payload }

    case 'ANSWER_DASS_QUESTION':
      return {
        ...state,
        dassAnswers: { ...state.dassAnswers, [action.payload.questionId]: action.payload.answerIndex },
      }

    case 'NEXT_DASS_QUESTION':
      return { ...state, currentDASSIndex: state.currentDASSIndex + 1 }

    case 'PREV_DASS_QUESTION':
      return { ...state, currentDASSIndex: Math.max(0, state.currentDASSIndex - 1) }

    case 'SET_DASS_SCORES':
      return { ...state, dassScores: action.payload }

    case 'SET_EXTENDED_PHASE':
      return { ...state, extendedPhase: action.payload }

    case 'ANSWER_PART2_DASS_QUESTION':
      return {
        ...state,
        part2DassAnswers: { ...state.part2DassAnswers, [action.payload.questionId]: action.payload.answerIndex },
      }

    case 'NEXT_PART2_DASS_QUESTION':
      return { ...state, currentPart2DASSIndex: state.currentPart2DASSIndex + 1 }

    case 'PREV_PART2_DASS_QUESTION':
      return { ...state, currentPart2DASSIndex: Math.max(0, state.currentPart2DASSIndex - 1) }

    case 'ANSWER_PROFILE_QUESTION':
      return {
        ...state,
        profileAnswers: { ...state.profileAnswers, [action.payload.questionId]: action.payload.answer },
      }

    case 'NEXT_PROFILE_QUESTION':
      return { ...state, currentProfileIndex: state.currentProfileIndex + 1 }

    case 'PREV_PROFILE_QUESTION':
      return { ...state, currentProfileIndex: Math.max(0, state.currentProfileIndex - 1) }

    case 'COMPLETE_EXTENDED_ASSESSMENT':
      return { ...state, extendedAssessmentComplete: true }

    case 'SET_INVESTOR_METRICS':
      return { ...state, investorMetrics: action.payload }

    case 'SET_RESULTS_ID':
      return { ...state, resultsId: action.payload }

    case 'SET_SUBMITTED_EMAIL':
      return { ...state, submittedEmail: action.payload }

    case 'RESET_ASSESSMENT':
      return initialState

    default:
      return state
  }
}

// Restore an in-progress session so a reload never throws away answers.
// Results opened from an email link skip this.
function init(initial) {
  try {
    if (new URLSearchParams(window.location.search).has('results')) return initial
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (!raw) return initial
    return { ...initial, ...JSON.parse(raw) }
  } catch {
    return initial
  }
}

export function AssessmentProvider({ children }) {
  const [state, dispatch] = useReducer(assessmentReducer, initialState, init)

  useEffect(() => {
    try {
      if (state.currentScreen === 'welcome' && state.selectedAddictions.length === 0) {
        sessionStorage.removeItem(SESSION_KEY)
      } else {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(state))
      }
    } catch {
      // Storage unavailable (private mode, quota): the session just won't survive a reload.
    }
  }, [state])

  return <AssessmentContext.Provider value={{ state, dispatch }}>{children}</AssessmentContext.Provider>
}

export function useAssessment() {
  const context = useContext(AssessmentContext)
  if (!context) throw new Error('useAssessment must be used within AssessmentProvider')
  return context
}
